import React, { useCallback, useLayoutEffect, useRef } from "react";
import type { EditorElement } from "../../../editor/types";
import { useLocale } from "../../../locales";
import type { ElementFormState } from "../types";
import {
  MAX_EDITOR_CANVAS_HEIGHT,
  MAX_EDITOR_CANVAS_WIDTH,
} from "../lib/canvas";
import {
  resolveTextStyle,
  resolveFillInTheBlanksAppearance,
} from "../lib/appearance";
import type { Blank } from "../types";
import { renderCanvasElementContent } from "../lib/renderers";
import RichTextEditor from "./RichTextEditor";
import MathTypeEditorModal from "./MathTypeEditorModal";

interface CanvasElementProps {
  element: EditorElement;
  isSelected: boolean;
  selectedElementIds?: string[]; // For multi-select drag support
  onSelect: (elementId: string, event?: React.PointerEvent) => void;
  onDrag: (elementId: string, position: { x: number; y: number }) => void;
  onDragStart?: (elementId: string, position: { x: number; y: number }) => void;
  onDragEnd?: (elementId: string, position: { x: number; y: number }) => void;
  onResize: (
    elementId: string,
    size: { width: number; height?: number; minWidth?: number; minHeight?: number }
  ) => void;
  onContextMenu: (
    elementId: string,
    x: number,
    y: number,
    hasTextSelection?: boolean
  ) => void;
  onUpdate?: (elementId: string, data: Partial<EditorElement["data"]>) => void;
  canvasSize: { width: number; height: number };
  canvasExpandable?: boolean;
  canvasLockWidth?: boolean;
  canvasInnerPadding?: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  zoom?: number;
  allElements: EditorElement[];
  onDropTargetChange?: (
    targetId: string,
    sourceIds: string | string[],
    sourceId: string | null
  ) => void;
  onChoiceChange?: (
    elementId: string,
    optionId: string,
    isMultiple: boolean
  ) => void;
  formState?: ElementFormState;
}

type DragMode = "move" | "resize";

const CanvasElement: React.FC<CanvasElementProps> = ({
  element,
  isSelected,
  selectedElementIds = [],
  onSelect,
  onDrag,
  onDragStart,
  onDragEnd,
  onResize,
  onContextMenu,
  onUpdate,
  canvasSize,
  canvasExpandable = true,
  canvasLockWidth = false,
  canvasInnerPadding,
  zoom = 1,
  allElements,
  onDropTargetChange,
  onChoiceChange,
  formState,
}) => {
  const { t } = useLocale();
  const elementRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const [isEditingText, setIsEditingText] = React.useState(false);
  const [editingText, setEditingText] = React.useState("");
  const [isMathTypeModalOpen, setIsMathTypeModalOpen] = React.useState(false);
  const originalSizeRef = useRef<EditorElement["size"] | null>(null);
  const [, forceUpdate] = React.useReducer((x) => x + 1, 0);
  const rafIdRef = useRef<number | null>(null);
  const needsUpdateRef = useRef(false);
  const lastDragCallRef = useRef<number>(0);
  const dragThrottleMs = 16; // ~60fps
  const dragStateRef = useRef<null | {
    mode: DragMode;
    pointerId: number;
    startPointer: { x: number; y: number };
    startPosition: { x: number; y: number };
    startSize: { width: number; height: number };
    currentPosition: { x: number; y: number };
    currentSize: { width: number; height: number };
    minSize: { width: number; height: number };
    aspectRatioLocked: boolean;
  }>(null);

  // RequestAnimationFrame ile güncelleme
  const scheduleUpdate = useCallback(() => {
    if (needsUpdateRef.current || rafIdRef.current !== null) {
      return; // Zaten bir update planlandı
    }

    needsUpdateRef.current = true;
    rafIdRef.current = requestAnimationFrame(() => {
      if (needsUpdateRef.current) {
        forceUpdate(); // Force re-render
        needsUpdateRef.current = false;
      }
      rafIdRef.current = null;
    });
  }, [forceUpdate]);

  // Cleanup RAF on unmount
  React.useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, []);

  const getContentSize = useCallback(
    (
      forWidth: number,
      overrides?: { content?: string; htmlContent?: string }
    ) => {
      if (element.type === "text") {
        const data = element.data as { content?: string; htmlContent?: string };
        const textStyle = resolveTextStyle(element);
        const content = overrides?.content ?? data.content ?? t("canvasElement.fallbackContent");
        const htmlContent = overrides?.htmlContent ?? data.htmlContent;

        // Calculate height for given width
        const tempElement = document.createElement("div");
        tempElement.style.position = "absolute";
        tempElement.style.visibility = "hidden";
        tempElement.style.width = `${forWidth}px`;
        tempElement.style.whiteSpace = "pre-wrap";
        tempElement.style.wordBreak = "break-word";
        tempElement.style.fontFamily = textStyle.fontFamily;
        tempElement.style.fontSize = `${textStyle.fontSize}px`;
        tempElement.style.fontWeight = textStyle.fontWeight.toString();
        tempElement.style.fontStyle = textStyle.italic ? "italic" : "normal";
        tempElement.style.textDecoration = textStyle.underline
          ? "underline"
          : "none";
        tempElement.style.lineHeight = `${textStyle.lineHeight}`;
        tempElement.style.letterSpacing = `${textStyle.letterSpacing}px`;
        tempElement.style.textAlign = textStyle.textAlign;
        tempElement.style.textTransform = textStyle.textTransform;
        tempElement.style.padding = `${textStyle.paddingY}px ${textStyle.paddingX}px`;
        tempElement.style.border =
          textStyle.borderWidth > 0
            ? `${textStyle.borderWidth}px solid ${textStyle.borderColor}`
            : "none";
        tempElement.style.borderRadius = `${textStyle.borderRadius}px`;
        tempElement.style.backgroundColor = textStyle.backgroundColor;
        tempElement.style.boxSizing = "border-box";
        if (htmlContent) {
          tempElement.innerHTML = htmlContent;
        } else {
          tempElement.textContent = content;
        }

        document.body.appendChild(tempElement);
        const height = tempElement.offsetHeight;
        document.body.removeChild(tempElement);

        return {
          width: forWidth,
          height: Math.max(0, Math.ceil(height)),
        };
      }

      return {
        width: forWidth,
        height: 0,
      };
    },
    [element]
  );

  const getMinContentSize = useCallback(() => {
    if (element.type === "text") {
      const currentWidth = element.size.width;
      const contentSize = getContentSize(currentWidth);
      return {
        width: element.size.minWidth ?? 0,
        height: Math.max(element.size.minHeight ?? 0, contentSize.height),
      };
    }

    // Element'in size'ında tanımlı minWidth/minHeight değerlerini kullan
    return {
      width: element.size.minWidth ?? 0,
      height: element.size.minHeight ?? 0,
    };
  }, [element, getContentSize]);

  // Only set initial height when element is first created (no height set yet)
  React.useEffect(() => {
    if (element.type !== "text" || element.size.height) {
      return;
    }

    const currentWidth = element.size.width;
    const contentSize = getContentSize(currentWidth);

    onResize(element.id, {
      width: currentWidth,
      height: contentSize.height,
      minHeight: contentSize.height,
    });
  }, [element, getContentSize, onResize]);

  // Drag sırasında CSS transform kullan, aksi halde position kullan
  const getStyle = (): React.CSSProperties => {
    const state = dragStateRef.current;

    if (isDragging && state) {
      // Drag sırasında normal z-index + 1000 (matching point'ler için özel artış yok)
      const baseStyle: React.CSSProperties = {
        position: "absolute",
        top: 0,
        left: 0,
        zIndex: element.zIndex + 1000, // Drag sırasında en üstte
        overflow: "hidden",
        willChange: "transform",
      };

      if (state.mode === "move") {
        const translateX = state.currentPosition.x;
        const translateY = state.currentPosition.y;
        const rotation = element.rotation
          ? `rotate(${element.rotation}deg)`
          : "";

        return {
          ...baseStyle,
          width: element.size.width,
          height: element.size.height || "auto",
          transform:
            `translate(${translateX}px, ${translateY}px) ${rotation}`.trim(),
        };
      } else {
        // Resize mode
        const translateX = element.position.x;
        const translateY = element.position.y;
        const rotation = element.rotation
          ? `rotate(${element.rotation}deg)`
          : "";

        return {
          ...baseStyle,
          width: state.currentSize.width,
          height: state.currentSize.height,
          transform:
            `translate(${translateX}px, ${translateY}px) ${rotation}`.trim(),
        };
      }
    }

    // Normal state - use position
    // Canvas modunda normal z-index kullanılır (matching point'ler için özel artış yok)
    // Z-index artışı sadece preview/render modunda yapılır
    return {
      position: "absolute",
      top: element.position.y,
      left: element.position.x,
      width: element.size.width,
      height: element.size.height || "auto",
      transform: element.rotation
        ? `rotate(${element.rotation}deg)`
        : undefined,
      zIndex: element.zIndex,
      overflow: "hidden",
    };
  };

  const style = getStyle();

  const className = [
    "qe-canvas__element",
    `qe-canvas__element--${element.type}`,
    isSelected ? "qe-canvas__element--selected" : "",
    isDragging ? "qe-canvas__element--dragging" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const cursorRef = useRef("");

  const getResizeCursor = (event: React.PointerEvent<HTMLDivElement>) => {
    const rect = elementRef.current?.getBoundingClientRect();
    if (!rect) {
      return null;
    }
    const threshold = 14;
    const nearLeft = Math.abs(event.clientX - rect.left) <= threshold;
    const nearRight = Math.abs(rect.right - event.clientX) <= threshold;
    const nearTop = Math.abs(event.clientY - rect.top) <= threshold;
    const nearBottom = Math.abs(rect.bottom - event.clientY) <= threshold;

    if ((nearLeft && nearTop) || (nearRight && nearBottom)) {
      return "nwse-resize";
    }
    if ((nearRight && nearTop) || (nearLeft && nearBottom)) {
      return "nesw-resize";
    }
    return null;
  };

  const setCursor = (cursor: string | null) => {
    const next = cursor ?? "";
    if (cursorRef.current === next) {
      return;
    }
    cursorRef.current = next;
    if (elementRef.current) {
      elementRef.current.style.cursor = next;
    }
    document.body.style.cursor = next;
  };

  // Geometric shapes whose figure should stay proportional while resizing.
  // Mirrors SHAPES_WITH_LOCKED_DEFAULT in lib/renderers; the user can override
  // per element via data.lockAspectRatio.
  const shapeKeepsAspectRatio = (() => {
    if (element.type !== "shape") {
      return false;
    }
    const shapeData = element.data as {
      kind?: string;
      lockAspectRatio?: boolean;
    };
    const NATURAL_LOCKED = new Set([
      "circle",
      "square",
      "triangle",
      "pentagon",
      "hexagon",
      "octagon",
      "star",
      "rhombus",
      "heart",
      "cube",
      "cylinder",
      "cone",
      "sphere",
      "pyramid",
    ]);
    if (typeof shapeData.lockAspectRatio === "boolean") {
      return shapeData.lockAspectRatio;
    }
    return shapeData.kind ? NATURAL_LOCKED.has(shapeData.kind) : false;
  })();

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    // Skip pointer handling when editing text to allow text selection
    if (isEditingText) {
      return;
    }

    if (event.pointerType === "mouse" && event.button !== 0) {
      return;
    }

    const target = event.target as HTMLElement;

    // Check if pointer is near the element edges (resize zone)
    const resizeCursor = getResizeCursor(event);
    const isResizeHandle = Boolean(resizeCursor);

    // For locked tables, allow selection but prevent drag/resize
    const isLockedTable = element.type === "table" && (element.data as any).locked;

    // Allow clicks on other interactive elements (but not drag handle or resize edges)
    if (
      target.closest("input, textarea, button, select, a") &&
      !isResizeHandle
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    // For locked tables, only allow selection (no drag/resize)
    if (isLockedTable) {
      // Select the table and clear any selected cell element
      onSelect(element.id, event);
      if (onUpdate) {
        onUpdate(element.id, { selectedCellElementId: undefined });
      }
      // Don't allow drag or resize for locked tables
      return;
    }

    const mode: DragMode = isResizeHandle ? "resize" : "move";

    // Only select if not already selected (to preserve multi-select)
    // If element is already selected and we're in multi-select mode, don't change selection
    if (!isSelected || (selectedElementIds.length <= 1)) {
      onSelect(element.id, event);

      // If this is a table being selected directly (not through a cell element),
      // clear the selectedCellElementId to show table properties
      if (element.type === "table" && onUpdate) {
        onUpdate(element.id, { selectedCellElementId: undefined });
      }
    }

    const rect = elementRef.current?.getBoundingClientRect();
    const baseWidth = element.size.width ?? rect?.width ?? 200;
    const baseHeight =
      element.size.height ??
      elementRef.current?.offsetHeight ??
      rect?.height ??
      baseWidth;

    // Element tipine göre minimum boyutu al
    const minContentSize = getMinContentSize();

    dragStateRef.current = {
      mode,
      pointerId: event.pointerId,
      startPointer: { x: event.clientX, y: event.clientY },
      startPosition: { ...element.position },
      startSize: { width: baseWidth, height: baseHeight },
      currentPosition: { ...element.position },
      currentSize: { width: baseWidth, height: baseHeight },
      minSize: minContentSize,
      aspectRatioLocked:
        mode === "resize" &&
        (event.shiftKey || shapeKeepsAspectRatio) &&
        baseWidth > 0 &&
        baseHeight > 0,
    };

    setIsDragging(true);
    elementRef.current?.setPointerCapture(event.pointerId);

    if (mode === "resize") {
      setCursor(resizeCursor || "nwse-resize");
    } else {
      setCursor(null);
    }
    
    // Notify parent about drag start (for multi-select)
    if (onDragStart && mode === "move") {
      onDragStart(element.id, { ...element.position });
    }
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const state = dragStateRef.current;
    if (!state) {
      if (!isEditingText) {
        setCursor(getResizeCursor(event));
      }
      return;
    }
    if (event.pointerId !== state.pointerId) {
      return;
    }

    event.preventDefault();

    if (state.mode === "resize") {
      const wantsAspectRatioLock =
        (event.shiftKey || shapeKeepsAspectRatio) &&
        state.currentSize.width > 0 &&
        state.currentSize.height > 0;

      if (state.aspectRatioLocked !== wantsAspectRatioLock) {
        state.startPointer = { x: event.clientX, y: event.clientY };
        state.startSize = { ...state.currentSize };
        state.startPosition = { ...state.currentPosition };
        state.aspectRatioLocked = wantsAspectRatioLock;
      }
    }

    const deltaX = (event.clientX - state.startPointer.x) / zoom;
    const deltaY = (event.clientY - state.startPointer.y) / zoom;

    // Convert rotation to radians for calculation
    const rotation = element.rotation || 0;
    const rad = (rotation * Math.PI) / 180;
    const absCos = Math.abs(Math.cos(rad));
    const absSin = Math.abs(Math.sin(rad));
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);

    // Get inner padding and canvas limits
    const innerPaddingLeft = canvasInnerPadding?.left || 0;
    const innerPaddingTop = canvasInnerPadding?.top || 0;
    const innerPaddingRight = canvasInnerPadding?.right || 0;
    const innerPaddingBottom = canvasInnerPadding?.bottom || 0;

    // Use actual canvas size from props for constraints
    const maxCanvasWidth = canvasSize.width;
    const maxCanvasHeight = canvasSize.height;

    // Helper to calculate AABB and check bounds
    // Always enforce left and top bounds (canvas origin is fixed)
    // Right and bottom bounds are only enforced if canvas is not expandable or at max size
    const checkBounds = (
      width: number,
      height: number,
      x: number,
      y: number
    ) => {
      const rotatedWidth = width * absCos + height * absSin;
      const rotatedHeight = width * absSin + height * absCos;

      const offsetX = (width - rotatedWidth) / 2;
      const offsetY = (height - rotatedHeight) / 2;

      const minX = x + offsetX; // Visual Left
      const maxX = x + width - offsetX; // Visual Right
      const minY = y + offsetY; // Visual Top
      const maxY = y + height - offsetY; // Visual Bottom

      const MAX_WIDTH = MAX_EDITOR_CANVAS_WIDTH;
      const MAX_HEIGHT = MAX_EDITOR_CANVAS_HEIGHT;
      const isAtMaxSize =
        maxCanvasWidth >= MAX_WIDTH || maxCanvasHeight >= MAX_HEIGHT;

      // Always check left and top bounds
      const violationLeft = Math.max(0, innerPaddingLeft - minX);
      const violationTop = Math.max(0, innerPaddingTop - minY);

      // Genişlik sabit olduğu için sağ sınır her zaman kontrol edilir
      // Alt sınır sadece expandable kapalıysa veya max boyuttaysa kontrol edilir
      const violationRight = Math.max(0, maxX - (maxCanvasWidth - innerPaddingRight));
      const violationBottom =
        !canvasExpandable || isAtMaxSize
          ? Math.max(0, maxY - (maxCanvasHeight - innerPaddingBottom))
          : 0;

      const isInvalid =
        violationLeft > 0.01 ||
        violationRight > 0.01 ||
        violationTop > 0.01 ||
        violationBottom > 0.01;
      return {
        isInvalid,
        violation:
          violationLeft + violationRight + violationTop + violationBottom,
      };
    };

    if (state.mode === "move") {
      // Calculate Position
      const rawX = state.startPosition.x + deltaX;
      const rawY = state.startPosition.y + deltaY;

      // Get element dimensions (accurate start size)
      const elementWidth = state.startSize.width;
      const elementHeight = state.startSize.height;

      // Calculate Axis-Aligned Bounding Box (AABB) dimensions for the rotated element
      const rotatedWidth = elementWidth * absCos + elementHeight * absSin;
      const rotatedHeight = elementWidth * absSin + elementHeight * absCos;

      // Calculate visual offset due to rotation
      // The visual top-left corner is shifted relative to the logical (x,y) top-left
      const offsetX = (elementWidth - rotatedWidth) / 2;
      const offsetY = (elementHeight - rotatedHeight) / 2;

      // Constraint Bounds based on Visual Edges (AABB)
      // Always enforce left and top bounds (canvas doesn't expand left/up)
      // Right boundary: enforce if canvas is not expandable, width is locked, or max width reached
      // Bottom boundary: enforce if canvas is not expandable or max height reached
      const MAX_WIDTH = MAX_EDITOR_CANVAS_WIDTH;
      const MAX_HEIGHT = MAX_EDITOR_CANVAS_HEIGHT;
      const isAtMaxWidth = maxCanvasWidth >= MAX_WIDTH;
      const isAtMaxHeight = maxCanvasHeight >= MAX_HEIGHT;
      // Genişlik sabit olduğu için sağ sınır her zaman uygulanır
      const enforceRightBoundary = true;
      // Enforce bottom boundary if: canvas is not expandable or max height reached
      const enforceBottomBoundary = !canvasExpandable || isAtMaxHeight;

      // Always enforce left and top bounds (canvas origin is fixed)
      const minX = innerPaddingLeft - offsetX;
      const minY = innerPaddingTop - offsetY;

      let nextX = Math.max(minX, rawX);
      let nextY = Math.max(minY, rawY);

      // Calculate max bounds
      const maxX =
        maxCanvasWidth - innerPaddingRight - elementWidth + offsetX;
      const maxY =
        maxCanvasHeight - innerPaddingBottom - elementHeight + offsetY;

      // Genişlik sabit - sağ sınır her zaman uygulanır
      nextX = Math.min(nextX, maxX);

      // Enforce bottom boundary if needed
      if (enforceBottomBoundary) {
        nextY = Math.min(nextY, maxY);
      } else {
        // Canvas is expandable and not at max height
        // Allow bottom overflow (canvas will expand) but prevent going too far
        if (rawY <= maxY) {
          nextY = Math.min(nextY, maxY);
        }
      }

      // Update ref
      state.currentPosition = { x: nextX, y: nextY };
      
      // Call onDrag during move for real-time updates
      // handleMoveElement will handle multi-select logic
      onDrag(element.id, { x: nextX, y: nextY });
      
      scheduleUpdate();
    } else {
      // Resize Logic
      const localDeltaX = deltaX * cos + deltaY * sin;
      const localDeltaY = -deltaX * sin + deltaY * cos;
      const minWidth = Math.max(10, state.minSize.width || 0);
      const minHeight = Math.max(10, state.minSize.height || 0);
      const shouldLockAspectRatio =
        state.aspectRatioLocked &&
        state.startSize.width > 0 &&
        state.startSize.height > 0;

      // Proposed dimensions without constraints
      let proposedWidth = state.startSize.width + localDeltaX;
      let proposedHeight = state.startSize.height + localDeltaY;

      if (shouldLockAspectRatio) {
        const widthScale =
          (state.startSize.width + localDeltaX) / state.startSize.width;
        const heightScale =
          (state.startSize.height + localDeltaY) / state.startSize.height;
        const dominantScale =
          Math.abs(widthScale - 1) >= Math.abs(heightScale - 1)
            ? widthScale
            : heightScale;
        const minScale = Math.max(
          minWidth / state.startSize.width,
          minHeight / state.startSize.height
        );
        const lockedScale = Math.max(minScale, dominantScale);
        proposedWidth = state.startSize.width * lockedScale;
        proposedHeight = state.startSize.height * lockedScale;
      } else {
        // Apply minimum size constraints independently when aspect ratio is unlocked.
        proposedWidth = Math.max(minWidth, proposedWidth);
        proposedHeight = Math.max(minHeight, proposedHeight);
      }

      // Function to calculate state at a given interpolation factor t (0 to 1)
      // t=0 is start state, t=1 is proposed state
      const calculateStateAt = (t: number) => {
        // Interpolate size
        const w =
          state.startSize.width + (proposedWidth - state.startSize.width) * t;
        const h =
          state.startSize.height +
          (proposedHeight - state.startSize.height) * t;

        // Calculate compensation at this size
        const dw = w - state.startSize.width;
        const dh = h - state.startSize.height;

        const dxComp = -(dw / 2) * (1 - cos) - (dh / 2) * sin;
        const dyComp = -(dh / 2) * (1 - cos) + (dw / 2) * sin;

        const x = state.startPosition.x + dxComp;
        const y = state.startPosition.y + dyComp;

        return { width: w, height: h, x, y };
      };

      // Calculate target state first
      const targetState = calculateStateAt(1);
      let finalState = targetState;

      // Always check bounds (left/top are always enforced, right/bottom only if not expandable or at max size)
      const startCheck = checkBounds(
        state.startSize.width,
        state.startSize.height,
        state.startPosition.x,
        state.startPosition.y
      );
      const targetCheck = checkBounds(
        targetState.width,
        targetState.height,
        targetState.x,
        targetState.y
      );

      // Only apply constraints if there are violations
      if (targetCheck.isInvalid || startCheck.isInvalid) {
        // If target is invalid, and start was valid (or "more" valid), we clamp.
        // If start was already invalid, we allow resize if it reduces violation,
        // otherwise we clamp to start?
        // For simplicity: if target invalid, try to find boundary.

        if (targetCheck.isInvalid) {
          if (!startCheck.isInvalid) {
            // Start valid -> Target invalid. Find boundary.
            // Binary search for t in [0, 1]
            let low = 0;
            let high = 1;
            for (let i = 0; i < 6; i++) {
              const mid = (low + high) / 2;
              const s = calculateStateAt(mid);
              const check = checkBounds(s.width, s.height, s.x, s.y);
              if (check.isInvalid) {
                high = mid;
              } else {
                low = mid;
              }
            }
            // use low (valid)
            finalState = calculateStateAt(low);
          } else {
            // Start was already invalid.
            // Allow resize ONLY if it reduces violation.
            if (targetCheck.violation < startCheck.violation) {
              finalState = targetState;
            } else {
              // Block resize
              finalState = calculateStateAt(0);
            }
          }
        }

        // Ensure left and top bounds are always respected (even if resize is allowed)
        const rotatedWidthFinal =
          finalState.width * absCos + finalState.height * absSin;
        const rotatedHeightFinal =
          finalState.width * absSin + finalState.height * absCos;
        const offsetXFinal = (finalState.width - rotatedWidthFinal) / 2;
        const offsetYFinal = (finalState.height - rotatedHeightFinal) / 2;
        const minXFinal = finalState.x + offsetXFinal;
        const minYFinal = finalState.y + offsetYFinal;

        // Clamp position to ensure left and top bounds
        if (minXFinal < innerPaddingLeft) {
          finalState.x = innerPaddingLeft - offsetXFinal;
        }
        if (minYFinal < innerPaddingTop) {
          finalState.y = innerPaddingTop - offsetYFinal;
        }
      }

      // Update ref
      state.currentSize = {
        width: finalState.width,
        height: finalState.height,
      };
      state.currentPosition = { x: finalState.x, y: finalState.y };
      scheduleUpdate();
    }
  };

  const handlePointerEnd = (event: React.PointerEvent<HTMLDivElement>) => {
    const state = dragStateRef.current;
    if (!state || event.pointerId !== state.pointerId) {
      return;
    }

    // Drag bittiğinde Redux state'i güncelle
    if (state.mode === "move") {
      // Finalize position (onDrag is already called during move, but we call it once more to ensure final state)
      onDrag(element.id, {
        x: Math.round(state.currentPosition.x),
        y: Math.round(state.currentPosition.y),
      });
      if (onDragEnd) {
        onDragEnd(element.id, {
          x: Math.round(state.currentPosition.x),
          y: Math.round(state.currentPosition.y),
        });
      }
    } else {
      onResize(element.id, {
        width: Math.round(state.currentSize.width),
        height: Math.round(state.currentSize.height),
      });
    }

    setIsDragging(false);
    dragStateRef.current = null;
    elementRef.current?.releasePointerCapture(event.pointerId);
    setCursor(null);
  };

  // Auto-resize when content changes (only expand, never shrink below content)
  useLayoutEffect(() => {
    if (element.type !== "text") {
      return;
    }
    // Don't auto-resize during drag operations
    if (dragStateRef.current) {
      return;
    }

    const currentWidth = element.size.width;
    const currentHeight = element.size.height;
    const currentMinHeight = element.size.minHeight;

    // Calculate what the height should be for current width
    const contentSize = getContentSize(currentWidth);
    const contentHeight = contentSize.height;

    // Expand to fit content, but don't auto-shrink
    const nextHeight =
      currentHeight === undefined || currentHeight < contentHeight
        ? contentHeight
        : currentHeight;

    if (nextHeight !== currentHeight || currentMinHeight !== contentHeight) {
      onResize(element.id, {
        width: currentWidth,
        height: nextHeight,
        minHeight: contentHeight,
      });
    }
  }, [element, getContentSize, onResize]);

  const handleContextMenuEvent = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();

      const selection = window.getSelection();
      const hasSelection = !!(selection && !selection.isCollapsed);

      onContextMenu(element.id, event.clientX, event.clientY, hasSelection);
    },
    [element.id, onContextMenu]
  );

  const handleDragStart = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      // Skip drag when editing text
      if (isEditingText) {
        return;
      }

      // Allow drag-source elements to use their own drag handlers
      const target = event.target as HTMLElement;
      if (
        target.closest(".qe-drag-source") ||
        target.closest(".qe-paint-swatch")
      ) {
        return;
      }

      // Prevent native drag behavior for other elements in canvas mode
      event.preventDefault();
      event.stopPropagation();
    },
    [isEditingText]
  );

  const handleDoubleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (
        (element.type === "text" || element.type === "fill-in-the-blanks") &&
        onUpdate
      ) {
        event.preventDefault();
        event.stopPropagation();

        // If already editing, keep current editingText, otherwise use element's content
        if (!isEditingText) {
          const data = element.data as { content?: string };
          const currentContent = data.content || "";
          setEditingText(currentContent);
          originalSizeRef.current = { ...element.size };
        }
        setIsEditingText(true);

        // Focus textarea after state update
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.focus();
            textareaRef.current.select();
          }
        }, 0);
      }
    },
    [element, onUpdate, isEditingText]
  );

  const handleTextSave = useCallback(() => {
    if (
      onUpdate &&
      (element.type === "text" || element.type === "fill-in-the-blanks")
    ) {
      if (element.type === "fill-in-the-blanks") {
        // Parse blanks and sync IDs
        const matches = editingText.match(/\[(.*?)\]/g) || [];
        const currentBlanks = ((element.data as any).blanks as Blank[]) || [];

        // Match by position/index to preserve ID and appearance even if value changes
        const newBlanks: Blank[] = matches.map((match, index) => {
          const value = match.slice(1, -1); // Remove brackets
          const existing = currentBlanks[index];
          return {
            id:
              existing?.id ||
              `blank-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            value,
            appearance: existing?.appearance, // Preserve appearance by position
          };
        });

        onUpdate(element.id, {
          content: editingText,
          blanks: newBlanks,
        });
      } else {
        onUpdate(element.id, { content: editingText });
      }
    }
    originalSizeRef.current = null;
    setIsEditingText(false);
  }, [onUpdate, element, editingText]);

  // Handler for RichTextEditor save (text elements only)
  const handleRichTextSave = useCallback(
    (htmlContent: string, plainText: string) => {
      if (onUpdate && element.type === "text") {
        onUpdate(element.id, {
          content: plainText,
          htmlContent: htmlContent,
        });
      }
      const contentSize = getContentSize(element.size.width, {
        content: plainText,
        htmlContent,
      });
      const currentHeight = element.size.height;
      const nextHeight =
        currentHeight === undefined || currentHeight < contentSize.height
          ? contentSize.height
          : currentHeight;
      onResize(element.id, {
        width: element.size.width,
        height: nextHeight,
        minHeight: contentSize.height,
      });
      originalSizeRef.current = null;
      setIsEditingText(false);
    },
    [onUpdate, element, getContentSize, onResize]
  );

  const handleRichTextContentChange = useCallback(
    (payload: { htmlContent: string; plainText: string; height: number }) => {
      if (element.type !== "text") {
        return;
      }

      const contentSize = getContentSize(element.size.width, {
        content: payload.plainText,
        htmlContent: payload.htmlContent,
      });
      const contentHeight = Math.max(0, Math.ceil(contentSize.height));
      const currentHeight = element.size.height;
      const currentMinHeight = element.size.minHeight;
      const nextHeight =
        currentHeight === undefined || currentHeight < contentHeight
          ? contentHeight
          : currentHeight;

      if (nextHeight !== currentHeight || currentMinHeight !== contentHeight) {
        onResize(element.id, {
          width: element.size.width,
          height: nextHeight,
          minHeight: contentHeight,
        });
      }
    },
    [element, getContentSize, onResize]
  );

  const handleTextCancel = useCallback(() => {
    const data = element.data as { content?: string };
    setEditingText(data.content || "");
    if (originalSizeRef.current) {
      onResize(element.id, { ...originalSizeRef.current });
      originalSizeRef.current = null;
    }
    setIsEditingText(false);
  }, [element, onResize]);

  const handleTextChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setEditingText(event.target.value);
    },
    []
  );

  const handleTextKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Prevent event from bubbling to parent element
      event.stopPropagation();

      if (event.key === "Enter" && !event.shiftKey) {
        // For text elements, Enter saves. For fill-in-the-blanks, we might want to allow newlines?
        // Actually, fill-in-the-blanks usually supports multiline text.
        // Let's allow Shift+Enter for newlines in both, and Enter to save in both for consistency.
        event.preventDefault();
        handleTextSave();
      } else if (event.key === "Escape") {
        event.preventDefault();
        handleTextCancel();
      }
    },
    [handleTextSave, handleTextCancel]
  );

  const handleTextBlur = useCallback(() => {
    handleTextSave();
  }, [handleTextSave]);

  // Geometric (naturally framed) shapes use only the outline selection
  // indicator; non-geometric shapes (rectangle, ellipse, arrow, line…) keep
  // the dashed selection box. CSS reads this attribute.
  const shapeKindForBorder =
    element.type === "shape"
      ? (element.data as { kind?: string }).kind ?? "rectangle"
      : undefined;
  const SHAPE_KINDS_GEOMETRIC = new Set([
    "circle",
    "square",
    "triangle",
    "right-triangle",
    "pentagon",
    "hexagon",
    "octagon",
    "star",
    "rhombus",
    "heart",
    "crescent",
    "cube",
    "cylinder",
    "cone",
    "sphere",
    "pyramid",
  ]);
  const dataShapeGeometric =
    shapeKindForBorder && SHAPE_KINDS_GEOMETRIC.has(shapeKindForBorder)
      ? "true"
      : undefined;

  return (
    <div
      role="button"
      tabIndex={0}
      className={className}
      style={style}
      ref={elementRef}
      data-shape-geometric={dataShapeGeometric}
      onMouseDown={(event) => {
        // Skip selection handling when editing text to allow text selection
        if (isEditingText) {
          return;
        }
        // Only select on direct mouse down on element wrapper, not during drag
        if (event.button === 0) {
          // Left click only
          event.stopPropagation(); // Prevent canvas deselect
          onSelect(element.id);
        }
      }}
      onKeyDown={(event) => {
        // Don't handle key events when editing text
        if (isEditingText) {
          return;
        }
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect(element.id);
        }
      }}
      onContextMenu={handleContextMenuEvent}
      onDoubleClick={handleDoubleClick}
      onDragStart={handleDragStart}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
      onPointerLeave={() => {
        if (!dragStateRef.current) {
          setCursor(null);
        }
      }}
    >
      {(element.type === "text" || element.type === "fill-in-the-blanks") &&
      isEditingText
        ? (() => {
            // For text elements, use RichTextEditor
            if (element.type === "text") {
              const textStyle = resolveTextStyle(element);
              const data = element.data as {
                content?: string;
                htmlContent?: string;
              };
              // Use htmlContent if available, otherwise fall back to content
              const initialContent = data.htmlContent || data.content || "";

              const editorStyle: React.CSSProperties = {
                width: "100%",
                height: "100%",
                fontFamily: textStyle.fontFamily,
                fontSize: `${textStyle.fontSize}px`,
                fontWeight: textStyle.fontWeight,
                color: textStyle.color,
                textAlign: textStyle.textAlign,
                lineHeight: textStyle.lineHeight,
                letterSpacing: `${textStyle.letterSpacing}px`,
                backgroundColor: textStyle.backgroundColor,
                padding: `${textStyle.paddingY}px ${textStyle.paddingX}px`,
                borderRadius: textStyle.borderRadius,
                border:
                  textStyle.borderWidth > 0
                    ? `${textStyle.borderWidth}px solid ${textStyle.borderColor}`
                    : "none",
                textTransform: textStyle.textTransform,
                boxShadow: textStyle.shadow,
                boxSizing: "border-box",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              };

              return (
                <RichTextEditor
                  initialContent={initialContent}
                  style={editorStyle}
                  onSave={handleRichTextSave}
                  onCancel={handleTextCancel}
                  onContentChange={handleRichTextContentChange}
                />
              );
            }

            // For fill-in-the-blanks, keep using textarea
            const appearance = resolveFillInTheBlanksAppearance(element);
            const textStyle = {
              fontFamily: appearance.fontFamily,
              fontSize: appearance.fontSize,
              fontWeight: appearance.fontWeight,
              color: appearance.color,
              lineHeight: appearance.lineHeight,
              backgroundColor: appearance.backgroundColor,
              paddingX: appearance.paddingX,
              paddingY: appearance.paddingY,
              borderRadius: appearance.borderRadius,
              borderWidth: appearance.borderWidth,
              borderColor: appearance.borderColor,
              textAlign: appearance.textAlign,
              shadow: appearance.shadow,
              textTransform: appearance.textTransform,
              letterSpacing: appearance.letterSpacing,
              italic: appearance.italic,
              underline: appearance.underline,
            };

            return (
              <textarea
                ref={textareaRef}
                value={editingText}
                onChange={handleTextChange}
                onKeyDown={handleTextKeyDown}
                onBlur={handleTextBlur}
                style={{
                  width: "100%",
                  height: "100%",
                  outline: "none",
                  background: "transparent",
                  resize: "none",
                  fontFamily: textStyle.fontFamily,
                  fontSize: `${textStyle.fontSize}px`,
                  fontWeight: textStyle.fontWeight,
                  fontStyle: textStyle.italic ? "italic" : "normal",
                  textDecoration: textStyle.underline ? "underline" : "none",
                  color: textStyle.color,
                  textAlign: textStyle.textAlign,
                  lineHeight: textStyle.lineHeight,
                  letterSpacing: `${textStyle.letterSpacing}px`,
                  backgroundColor: textStyle.backgroundColor,
                  padding: `${textStyle.paddingY}px ${textStyle.paddingX}px`,
                  borderRadius: textStyle.borderRadius,
                  border:
                    textStyle.borderWidth > 0
                      ? `${textStyle.borderWidth}px solid ${textStyle.borderColor}`
                      : "none",
                  textTransform: textStyle.textTransform,
                  boxShadow: textStyle.shadow,
                  boxSizing: "border-box",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              />
            );
          })()
        : renderCanvasElementContent(element, {
            mode: "canvas",
            allElements,
            onDragDropChange: onDropTargetChange,
            onChoiceChange: onChoiceChange,
            onUpdate: onUpdate,
            onSelect: onSelect,
            onContextMenu: (elemId: string, x: number, y: number) => {
              onContextMenu(elemId, x, y);
            },
            onEditMathType: () => {
              setIsMathTypeModalOpen(true);
            },
            formState:
              formState ||
              (element.type === "drop-target"
                ? {
                    [element.id]:
                      ((element.data as any).droppedItems as
                        | string[]
                        | undefined) || [],
                  }
                : undefined),
          })}
      {/* MathType Editor Modal */}
      {(element.type === "mathtype" || element.type === "mathlive") && (
        <MathTypeEditorModal
          isOpen={isMathTypeModalOpen}
          initialValue={
            (element.data.style as any)?.mathml ||
            (element.data.style as any)?.latex ||
            ""
          }
          onClose={() => setIsMathTypeModalOpen(false)}
          onSave={(mathml: string) => {
            if (onUpdate) {
              onUpdate(element.id, {
                style: {
                  ...(element.data.style || {}),
                  mathml: mathml,
                },
              });
            }
          }}
        />
      )}
      {/* Visible resize handles at the four corners. Purely visual — the
          existing corner hit-test in handlePointerDown starts the resize; these
          just make the grab points discoverable (esp. for shapes/trackpad). */}
      {isSelected &&
        !isEditingText &&
        selectedElementIds.length <= 1 &&
        !(element.type === "table" && (element.data as any).locked) &&
        (
          [
            { top: -6, left: -6, cursor: "nwse-resize" },
            { top: -6, right: -6, cursor: "nesw-resize" },
            { bottom: -6, left: -6, cursor: "nesw-resize" },
            { bottom: -6, right: -6, cursor: "nwse-resize" },
          ] as Array<React.CSSProperties>
        ).map((pos, index) => (
          <span
            key={`resize-handle-${index}`}
            aria-hidden
            className="qe-resize-handle"
            style={{
              position: "absolute",
              width: 11,
              height: 11,
              boxSizing: "border-box",
              background: "#ffffff",
              border: "1.5px solid #2563eb",
              borderRadius: 2,
              boxShadow: "0 1px 2px rgba(15, 23, 42, 0.35)",
              pointerEvents: "none",
              zIndex: 30,
              ...pos,
            }}
          />
        ))}
    </div>
  );
};


export default CanvasElement;
